import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from '@mui/material';
import useDialog from 'hooks/useDialog';
import { RejectedStatus } from '../ApplicationStatusControl';
import { SelectInput, SimpleForm, Toolbar, useRecordContext } from 'react-admin';
import { ProfessionalDisplayHelper } from 'utils/displayHelpers/professional';
import { IProfessional } from '@medely/types';
import { useFormContext } from 'react-hook-form';
import ScreenerAccountInput from 'components/Filter/ScreenerAccountInput';
import DateTimeInput from 'components/DateTimeInput';
import { getLatestActivePhoneScreening } from '../screeningUtils';

interface RejectItemProps {
  updateApplicationStatus: (val: any) => void;
}

const PROFESSIONAL_TYPE = {
  AGENCY: 'AGENCY',
  IRP: 'IRP',
  MARKETPLACE: 'MARKETPLACE',
} as const;

const reasonChoices = [
  'Conflict of Interest',
  'Deactivated Account',
  'Duplicate Account',
  'Falsifying Document',
  'No relevant experience',
  'Not a good fit - Bad Attitude',
  'Test Account',
];

const RejectToolBar = ({ closeDialog, requireScreeningFields }) => {
  const { getValues } = useFormContext();
  const values = getValues();
  const isDisabled = requireScreeningFields
    ? !values?.reject_reason || !values?.screener_account_id || !values?.scheduled_time
    : !values?.reject_reason;

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Button onClick={closeDialog}>Cancel</Button>
      <Button type="submit" variant="contained" color="primary" disabled={isDisabled}>
        Submit
      </Button>
    </Toolbar>
  );
};

const RejectItem = ({ updateApplicationStatus }: RejectItemProps) => {
  const { open, closeDialog, openDialog } = useDialog();

  return (
    <>
      <MenuItem onClick={openDialog}>Reject</MenuItem>
      <RejectDialog
        open={open}
        closeDialog={closeDialog}
        updateApplicationStatus={updateApplicationStatus}
      />
    </>
  );
};

const RejectDialog = ({ open, closeDialog, updateApplicationStatus }) => {
  const record: IProfessional = useRecordContext();
  const latestActivePhoneScreening = getLatestActivePhoneScreening(record);
  const isVerbalScreen = !!latestActivePhoneScreening;
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getProfessionalTypeString = (proType: string) => {
    const type = proType?.toUpperCase();
    if (type === PROFESSIONAL_TYPE.AGENCY) {
      const agencyName = record?.account.agency?.name;
      return `Agency${agencyName ? ` (${agencyName})` : ''}`;
    }
    if (type === PROFESSIONAL_TYPE.IRP) {
      const companyName = record?.account.company?.name;
      return `IRM${companyName ? ` (${companyName})` : ''}`;
    }
    if (type === PROFESSIONAL_TYPE.MARKETPLACE) {
      return 'Marketplace';
    } else {
      return 'professional';
    }
  };

  const fullName = (professional: IProfessional) => {
    return (
      professional?.account &&
      `${professional.account.first_name} ${professional.account.last_name}`
    );
  };

  const professionalType = record ? new ProfessionalDisplayHelper(record).type : '';
  const professionalName = record ? fullName(record) : '';

  const submitUpdateApplicationStatus = ({
    reject_reason,
    screener_account_id,
    scheduled_time,
  }) => {
    updateApplicationStatus(
      isVerbalScreen
        ? {
            application_status: RejectedStatus,
            reject_reason,
            professional_id: record?.id,
            screener_account_id,
            scheduled_time,
            reason: 'application',
            screening_type: 'phone',
          }
        : {
            application_status: RejectedStatus,
            reject_reason,
          },
    );
  };

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>Reject Reason</DialogTitle>
      <SimpleForm
        onSubmit={submitUpdateApplicationStatus}
        defaultValues={
          isVerbalScreen
            ? {
                screener_account_id: latestActivePhoneScreening?.screener_account_id ?? undefined,
                scheduled_time: latestActivePhoneScreening?.scheduled_time ?? undefined,
              }
            : undefined
        }
        toolbar={
          <RejectToolBar closeDialog={closeDialog} requireScreeningFields={isVerbalScreen} />
        }
      >
        <DialogContent>
          <Typography>
            Professional Name:{' '}
            <Typography component="span" color="error" fontWeight="bold">
              {professionalName}
            </Typography>
            <br />
            Professional Type:{' '}
            <Typography component="span" color="error" fontWeight="bold">
              {getProfessionalTypeString(professionalType)}
            </Typography>
          </Typography>
        </DialogContent>
        <DialogContent>
          <SelectInput
            fullWidth
            source="reject_reason"
            label="Select Reject Reason"
            choices={reasonChoices.map((reason) => ({ id: reason, name: reason }))}
          />
          {isVerbalScreen && (
            <>
              <ScreenerAccountInput
                source="screener_account_id"
                label="Screening Recruiter"
                fullWidth
                alwaysOn
              />
              <DateTimeInput
                label="Screening Date And Time"
                source="scheduled_time"
                time_zone={browserTimezone}
                ampm
              />
            </>
          )}
        </DialogContent>
      </SimpleForm>
    </Dialog>
  );
};

export default RejectItem;
